/* /portaal/facturen — mobile-first facturenoverzicht + KOT-jaaropgave download. */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { ArrowLeft, Wallet, Receipt, FileDown, Calculator, AlertCircle, CheckCircle2 } from "lucide-react";

const BACKEND_ROOT = API.replace(/\/api$/, "");
const MND = ["", "jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

export default function PortaalFacturenPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const nowYear = new Date().getFullYear();
  const [kotYear, setKotYear] = useState(nowYear - 1);
  const [kotBusy, setKotBusy] = useState(false);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    (async () => {
      try {
        const r = await api.get("/portaal/facturen");
        setInvoices(r.data || []);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
      finally { setLoading(false); }
    })();
  }, [status, user]);

  const totals = useMemo(() => {
    let open = 0, overdue = 0, paid = 0, cnt_open = 0, cnt_overdue = 0;
    for (const i of invoices) {
      const t = Number(i.total || 0);
      if (i.overtijd) { overdue += t; cnt_overdue += 1; }
      else if (i.openstaand) { open += t; cnt_open += 1; }
      else if (i.betaald) { paid += t; }
    }
    return { open, overdue, paid, cnt_open, cnt_overdue };
  }, [invoices]);

  const downloadKot = async () => {
    setKotBusy(true);
    try {
      const r = await api.get(`/portaal/kot/pdf?year=${kotYear}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `KOT-${kotYear}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`KOT-jaaropgave ${kotYear} gedownload`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setKotBusy(false); }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `factuur-${inv.factuurnummer || inv.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
  };

  if (status === "loading") return null;
  if (status !== "authed" || user?.role !== "ouder") return <Navigate to="/login" replace />;

  const totalOpenAll = totals.open + totals.overdue;
  const totalOpenCount = totals.cnt_open + totals.cnt_overdue;

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="portaal-facturen-page">
      <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/portaal")} data-testid="facturen-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="text-base font-semibold text-neutral-900">Facturen</div>
          <div className="text-xs text-neutral-500">Alles betaald? Perfect. Anders zie je hieronder wat openstaat.</div>
        </div>
      </div>

      {/* Openstaand badge */}
      <div className="px-4 pt-4">
        <Card className={`p-4 ${totalOpenAll > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="facturen-openstaand-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Openstaand totaal</div>
              <div className={`text-3xl font-display mt-1 ${totalOpenAll > 0 ? "text-red-800" : "text-emerald-800"}`}>
                {formatCurrency(totalOpenAll)}
              </div>
              <div className="text-xs text-neutral-600 mt-1">
                {totalOpenAll > 0
                  ? `${totalOpenCount} factuur${totalOpenCount !== 1 ? "en" : ""}${totals.cnt_overdue ? ` — waarvan ${totals.cnt_overdue} overtijd` : ""}`
                  : "Alles betaald — bedankt!"}
              </div>
            </div>
            <Wallet className={`h-10 w-10 ${totalOpenAll > 0 ? "text-red-400" : "text-emerald-400"}`} />
          </div>
        </Card>
      </div>

      {/* KOT-jaaropgave */}
      <div className="px-4 pt-4">
        <Card className="p-4 bg-white" data-testid="facturen-kot-card">
          <div className="flex items-start gap-3 mb-3">
            <Calculator className="h-6 w-6 text-[color:var(--sage)] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-neutral-900">KOT-jaaropgave</div>
              <div className="text-xs text-neutral-500 mt-0.5">Download een overzicht van gerealiseerde opvanguren per kind × maand voor de Belastingdienst.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(kotYear)} onValueChange={v => setKotYear(parseInt(v, 10))}>
              <SelectTrigger className="h-10 max-w-[120px]" data-testid="facturen-kot-year"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {[nowYear, nowYear - 1, nowYear - 2, nowYear - 3].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={downloadKot} disabled={kotBusy} className="h-10 text-white flex-1" style={{ background: "var(--sage)" }} data-testid="facturen-kot-download">
              <FileDown className="h-4 w-4 mr-1.5" /> {kotBusy ? "Genereren…" : "Download PDF"}
            </Button>
          </div>
          <div className="text-[11px] text-neutral-400 mt-2">Indicatief — controleer altijd tegen de factuur en Belastingdienst-portaal.</div>
        </Card>
      </div>

      {/* Facturen-lijst */}
      <div className="px-4 pt-4 space-y-2">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2 mb-1">Alle facturen ({invoices.length})</div>

        {loading && <div className="text-center text-sm text-neutral-500 py-6">Laden…</div>}
        {!loading && invoices.length === 0 && (
          <Card className="p-6 bg-white text-center" data-testid="facturen-empty">
            <Receipt className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
            <div className="text-sm text-neutral-600">Nog geen facturen beschikbaar.</div>
          </Card>
        )}

        {invoices.map(inv => {
          const period = inv.period_year && inv.period_month ? `${MND[inv.period_month]} ${inv.period_year}` : (inv.datum || "").slice(0, 10);
          const isOverdue = inv.overtijd;
          const isPaid = inv.betaald;
          const isOpen = inv.openstaand;
          return (
            <Card key={inv.id} className="p-3 bg-white flex items-center gap-3" data-testid={`facturen-row-${inv.id}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isPaid ? "bg-emerald-100 text-emerald-700" : isOverdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                {isPaid ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-medium text-neutral-900 truncate">{inv.kind_naam || "—"}</div>
                  {isPaid && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Betaald</Badge>}
                  {isOverdue && <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]">Overtijd</Badge>}
                  {isOpen && !isOverdue && <Badge className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">Openstaand</Badge>}
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {period} · <span className="font-mono">{inv.factuurnummer || inv.id.slice(0, 8)}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={`text-sm font-semibold ${isPaid ? "text-emerald-700" : isOverdue ? "text-red-700" : "text-neutral-900"}`}>
                  {formatCurrency(inv.total || 0)}
                </div>
                <button onClick={() => downloadInvoicePdf(inv)} className="text-[11px] text-[color:var(--sage)] hover:underline flex items-center gap-1 mt-0.5" data-testid={`facturen-pdf-${inv.id}`}>
                  <FileDown className="h-3 w-3" /> PDF
                </button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
